import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import WelcomePage from "./components/WelcomePage";
import Dashboard from "./Dashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import Register from "./components/Register";
import Pricing from "./components/Pricing";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Terms from "./components/Terms";
import PaymentPage from "./components/PaymentPage";
import TripViewerPage from "./components/TripViewerPage";
import { auth } from "./firebaseConfig";
import { UserProvider } from "./context/UserContext";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds

const AutoLogout = ({ user }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    let timer;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        signOut(auth)
          .then(() => {
            navigate("/"); // Redirect to welcome page
          })
          .catch((error) => {
            console.error("Auto logout error:", error);
            navigate("/"); // Redirect to welcome page
          });
      }, INACTIVITY_TIMEOUT);
    };

    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners for user activity
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity);

    // Start the timer
    resetTimer();

    // Cleanup
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity);
    };
  }, [user, navigate]);

  return null;
};

const TrackPageView = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);

  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => {
      console.log("App.js - Auth state changed:", u ? u.uid : "No user");
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    console.log("App.js - Loading authentication state...");
    return <div>Loading...</div>;
  }

  console.log("App.js - Rendering with user:", user ? user.uid : "No user");
  return (
    <UserProvider>
      <Router>
      <TrackPageView />
        <AutoLogout user={user} />
        <Routes>
          <Route path="/" element={<WelcomePage />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route
            path="/trip-viewer"
            element={
              <ProtectedRoute user={user}>
                <TripViewerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute user={user}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
        </Routes>
        <ToastContainer position="top-right" autoClose={2000} />
      </Router>
    </UserProvider>
  );
}

export default App;